'use client';

import { useState, useEffect, useCallback, useRef } from 'react';

/**
 * Custom hook for Google Places Autocomplete
 * Uses Places API (New) for both suggestions and details
 *
 * Features to minimize API requests and costs:
 * - Debouncing (400ms delay after user stops typing)
 * - Minimum character requirement (3 characters)
 * - Session tokens to group requests for billing
 * - Geographic restriction to US addresses only
 * - Type filtering to address results only
 */
export const useGooglePlaces = () => {
  const [isLoaded, setIsLoaded] = useState(false);
  const sessionTokenRef = useRef(null);
  const debounceTimerRef = useRef(null);

  // Initialize Google Places when API loads
  useEffect(() => {
    const checkGoogleMaps = () => {
      if (window.google?.maps?.places?.AutocompleteSuggestion) {
        // New API is ready
        sessionTokenRef.current = new window.google.maps.places.AutocompleteSessionToken();
        setIsLoaded(true);
      } else {
        // Check again in 100ms
        setTimeout(checkGoogleMaps, 100);
      }
    };
    checkGoogleMaps();
  }, []);

  /**
   * Get autocomplete suggestions with debouncing
   * Uses new AutocompleteSuggestion.fetchAutocompleteSuggestions()
   * Only makes API request after user stops typing for 400ms
   * Minimum 3 characters required
   */
  const getAutocompleteSuggestions = useCallback((input, callback) => {
    // Clear previous debounce timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // Return empty if not loaded or input too short
    if (!isLoaded || !input || input.length < 3) {
      callback([]);
      return;
    }

    // Debounce the API request
    debounceTimerRef.current = setTimeout(async () => {
      try {
        const request = {
          input: input,
          sessionToken: sessionTokenRef.current,
          includedRegionCodes: ['us'],
          includedPrimaryTypes: ['street_address', 'subpremise', 'premise']
        };
        const {
          suggestions
        } = await window.google.maps.places.AutocompleteSuggestion.fetchAutocompleteSuggestions(request);

        // Transform to match expected format for component compatibility
        const transformed = suggestions.filter(s => s.placePrediction).map(s_0 => ({
          place_id: s_0.placePrediction.placeId,
          placePrediction: s_0.placePrediction,
          structured_formatting: {
            main_text: s_0.placePrediction.mainText?.text || '',
            secondary_text: s_0.placePrediction.secondaryText?.text || ''
          }
        }));
        callback(transformed);
      } catch (error) {
        callback([]);
      }
    }, 400);
  }, [isLoaded]);

  /**
   * Get place details for selected address
   * Uses Place.fetchFields() from Places API (New)
   * After getting details, creates new session token for next search
   */
  const getPlaceDetails = useCallback(async (prediction, callback_0) => {
    if (!isLoaded) {
      callback_0(null);
      return;
    }
    try {
      let place;

      // If prediction has placePrediction object, use toPlace() for efficiency
      if (prediction.placePrediction?.toPlace) {
        place = prediction.placePrediction.toPlace();
      } else {
        // Fallback: create Place from placeId
        const placeId = typeof prediction === 'object' ? prediction.place_id || prediction.placeId : prediction;
        place = new window.google.maps.places.Place({
          id: placeId,
          requestedLanguage: 'en'
        });
      }
      await place.fetchFields({
        fields: ['addressComponents']
      });

      // Create new session token for next search
      sessionTokenRef.current = new window.google.maps.places.AutocompleteSessionToken();
      callback_0(place);
    } catch (error_0) {
      // Create new session token even on error
      sessionTokenRef.current = new window.google.maps.places.AutocompleteSessionToken();
      callback_0(null);
    }
  }, [isLoaded]);

  /**
   * Parse Google place result into address components
   * Handles Places API (New) format (addressComponents, longText/shortText)
   */
  const parseAddressComponents = useCallback(place_0 => {
    // New API uses addressComponents (camelCase)
    const components = place_0?.addressComponents;
    if (!components) {
      return null;
    }
    const addressData = {
      street_number: '',
      route: '',
      city: '',
      state: '',
      zip: '',
      country: ''
    };
    components.forEach(component => {
      const types = component.types;

      // New API uses longText/shortText instead of long_name/short_name
      if (types.includes('street_number')) {
        addressData.street_number = component.longText;
      } else if (types.includes('route')) {
        addressData.route = component.longText;
      } else if (types.includes('locality')) {
        addressData.city = component.longText;
      } else if (types.includes('administrative_area_level_1')) {
        addressData.state = component.shortText;
      } else if (types.includes('postal_code')) {
        addressData.zip = component.longText;
      } else if (types.includes('country')) {
        addressData.country = component.shortText;
      }
    });

    // Combine street number and route
    addressData.street_1 = `${addressData.street_number} ${addressData.route}`.trim();
    return addressData;
  }, []);

  // Cleanup debounce timer on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);
  return {
    isLoaded,
    getAutocompleteSuggestions,
    getPlaceDetails,
    parseAddressComponents
  };
};